import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Status } from '@tiendanube/common/src/enums';
import { Stack, Text } from '@tiendanube/components';
import { FEATURE_MONTHLY_ORDERS_EXPORT } from 'App/features';
import { PAYMENT_STATUS_EXPIRED_ENABLED } from 'App/featuresFlags';
import HelpLink from 'App/HelpLink';
import { useHasTags, useNavegate } from 'App/hooks';
import {
  ExportCard,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
} from 'commons/components';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import { HELP_LINKS_TO_EXPORT_ORDERS } from 'commons/constants';
import { useTranslationLanguage, useModal } from 'commons/hooks';
import { ORDERS_EXPORT_ORDERS } from 'config/upsellFlowSources';
import { useFeatureUsesByKey, useStatusFeatureByKey } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { ProductInterface } from 'domains/Orders/components/ProductSearcher';
import { trackingOrderListExport } from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import {
  OrdersExportFiltersModal,
  UpgradePlanAlert,
  ExportOrdersAlert,
} from './components';
import {
  useDownloadOrdersCsv,
  useOrdersExport,
  useOrdersExportFilters,
} from './hooks';
import {
  COMMA_SEPARATOR,
  ExportFiltersType,
  FilterStatusEnum,
  paymentProviderFilter,
  paymentMethodsFilter,
  statusFilter,
  shippingMethodFilter,
  paymentStatusFilter,
  fulfillmentStatusFilter,
  PIPE_SEPARATOR,
  originFilter,
} from '../../ordersService';
import { getOrdersAggregations } from '../../ordersSlice';
import { useFilterSelectedProducts } from '../OrderListPage/hooks/useFilterSelectedProducts';

function OrdersExportPage(): JSX.Element {
  const t = useTranslationOrders();
  const language = useTranslationLanguage();
  const { goBack } = useNavegate();
  const [hasExpiredTag] = useHasTags([PAYMENT_STATUS_EXPIRED_ENABLED]);
  const { isAvailable, unlimited, maxUse } = useStatusFeatureByKey(
    'monthly_orders_export',
  );
  const {
    featureUses,
    isLoading: isFeatureUsesLoading,
    getFeatureUses,
  } = useFeatureUsesByKey('monthly_orders_export');
  const [showFiltersModal, openFiltersModal, closeFiltersModal] = useModal();
  const [showUpsellAlert, setShowUpsellAlert] = useState(true);
  const [couponOptions, setCouponOptions] = useState<OptionType[]>([]);

  const {
    filters,
    totalResults,
    isLoading,
    getTotalOrders,
    refreshTotalOrders,
    removeFilter,
    clearFilters,
  } = useOrdersExport();

  const {
    exportOrders,
    isExporting,
    jobCsvDownloadStatus,
    resetExportingStatus,
    csvDownloadLinkUrl,
  } = useDownloadOrdersCsv();
  const { getFilterChips } = useOrdersExportFilters(filters, couponOptions);

  const aggregations = useSelector(getOrdersAggregations);
  const { selectedProducts, setSelectedProducts, serializeProducts } =
    useFilterSelectedProducts(filters.products, aggregations);

  useEffect(() => {
    refreshTotalOrders();
    getFeatureUses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = useUpsellFlow({
    title: t('exportPage.upsellTitle'),
    featureKey: FEATURE_MONTHLY_ORDERS_EXPORT,
    minValue: featureUses + 1,
    trackingSource: ORDERS_EXPORT_ORDERS,
    callback: () => {
      resetExportingStatus();
      exportOrders();
      trackingOrderListExport({
        orderStatusFilter: filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        shippingStatusFilter: filters.fulfillmentStatus.split(','),
        paymentStatusFilter: filters.paymentStatus.split(','),
        paymentMethodFilter: String(!!filters.paymentMethods),
        paymentProviderFilter: String(!!filters.paymentProvider),
        shippingFilter: String(!!filters.shippingMethod),
        channelFilter: String(!!filters.origin),
        couponIdsFilter: String(!!filters.couponIdsRaw),
        applicationFilter: String(!!filters.appId),
        isWholesaleFilter: String(!!filters.isWholesale),
        isSubscriptionFilter: String(!!filters.isSubscription),
      });
    },
  });

  const handleClearFilterButtonClick = () => {
    setSelectedProducts([]);
    clearFilters();
  };

  const handleModalConfirm = (
    filters: ExportFiltersType,
    selectedProducts: ProductInterface<true>[],
  ) => {
    setSelectedProducts(selectedProducts);
    getTotalOrders(filters);
  };

  const handleRemoveFilterChip = (key: string) => {
    if (
      key.startsWith(`${shippingMethodFilter}-`) ||
      key.startsWith(`${paymentStatusFilter}-`) ||
      key.startsWith(`${fulfillmentStatusFilter}-`) ||
      key.startsWith(`${paymentMethodsFilter}-`) ||
      key.startsWith(`${paymentProviderFilter}-`) ||
      key.startsWith(`${originFilter}-`)
    ) {
      const firstHyphenIndex = key.indexOf('-');
      const filterKey = key.substring(0, firstHyphenIndex);
      const filterValue = key.substring(firstHyphenIndex + 1);

      const currentValues = filters[filterKey] || '';

      const separator =
        filterKey === paymentProviderFilter ? PIPE_SEPARATOR : COMMA_SEPARATOR;

      let newValue = currentValues
        .split(separator)
        .filter((val) => val !== filterValue)
        .join(separator);

      // If removing 'voided' from paymentStatus without tag, also remove 'expired'
      if (
        filterKey === paymentStatusFilter &&
        filterValue === Status.VOIDED &&
        !hasExpiredTag
      ) {
        newValue = newValue
          .split(separator)
          .filter((val) => val !== Status.EXPIRED)
          .join(separator);
      }

      getTotalOrders({ ...filters, [filterKey]: newValue });
      return;
    }

    if (key.startsWith(`${statusFilter}-`)) {
      const firstHyphenIndex = key.indexOf('-');
      const filterKey = key.substring(0, firstHyphenIndex);
      getTotalOrders({ ...filters, [filterKey]: '' });
      return;
    }

    if (key.startsWith('variant-')) {
      const variantId = key.split('-')[1];
      setSelectedProducts((prev) =>
        prev.filter((p) => p.variantId !== variantId),
      );
    }
    if (key.startsWith('couponIdsRaw-')) {
      const couponIdToRemove = key.split('-')[1];
      if (!filters.couponIdsRaw) return;

      const remainingCouponIds = filters.couponIdsRaw
        .split(',')
        .filter((id) => id !== couponIdToRemove)
        .join(',');

      getTotalOrders({ ...filters, couponIdsRaw: remainingCouponIds });
      return;
    }
    removeFilter(key);
  };

  const handleDismissUpsellAlert = () => setShowUpsellAlert(false);

  const availableUses = maxUse - featureUses;
  const hasAvailableUses = availableUses > 0;
  const maxLimitReached = !unlimited && !hasAvailableUses;
  const disableExport = totalResults === 0 || isLoading;
  const cardTotalLabel =
    filters.status === FilterStatusEnum.EMPTY || filters.status === Status.OPEN
      ? t('exportPage.totalOpen')
      : t('exportPage.total');

  return (
    <IonPageStratus
      headerTop={<HeaderTop navigation={{ onClick: goBack }} />}
      headerContent={<HeaderContent title={t('exportPage.title')} />}
      width="small"
    >
      <Stack column align="stretch">
        <Stack.Item>
          <Text>{t('exportPage.subtitle')}</Text>
        </Stack.Item>
        <Stack.Item />
      </Stack>
      {!isFeatureUsesLoading && !unlimited && isAvailable && (
        <UpgradePlanAlert
          show={showUpsellAlert}
          availableUses={availableUses}
          isMaxExportReached={maxLimitReached}
          onClick={handleConfirm}
          onDismiss={handleDismissUpsellAlert}
        />
      )}
      <ExportOrdersAlert
        exportingStatus={jobCsvDownloadStatus}
        csvUrl={csvDownloadLinkUrl}
        resetExportingStatus={resetExportingStatus}
      />
      <Stack column align="stretch">
        <Stack.Item>
          <ExportCard
            filters={getFilterChips()}
            showClearFiltersButton
            onClearFilterButtonClick={handleClearFilterButtonClick}
            totalLabel={cardTotalLabel}
            total={totalResults}
            loadingTotal={isLoading}
            exporting={isExporting}
            disabled={disableExport}
            onRemoveFilter={handleRemoveFilterChip}
            onOpenFilters={openFiltersModal}
            onConfirm={handleConfirm}
          />
        </Stack.Item>
      </Stack>
      <HelpLink
        title={t('exportPage.helpLinkTitle')}
        previousValue=""
        currentViewTracking={t('exportPage.helpLinkTracking')}
        linkURL={HELP_LINKS_TO_EXPORT_ORDERS[language]}
        icon="both"
      />
      <OrdersExportFiltersModal
        show={showFiltersModal}
        filters={filters}
        onClose={closeFiltersModal}
        onConfirm={handleModalConfirm}
        selectedProducts={selectedProducts}
        serializeProducts={serializeProducts}
        onCouponOptionsLabel={setCouponOptions}
      />
    </IonPageStratus>
  );
}

export default OrdersExportPage;
